import { createContext, useContext, useMemo } from 'react';

/**
 * ModuleAccessContext
 *
 * Provides the effective module access (modules + per-module permissions)
 * to all components inside <Layout>.
 *
 * Context value shape:
 *   {
 *     modules: Set<string> | null,         // enabled module keys
 *     permissions: { [key]: { can_read, can_write, can_approve } },
 *     isAdmin: boolean,
 *   }
 *
 * Usage:
 *   const modules = useModuleAccess();              // Set<string> | null  (backward-compatible)
 *   const canSee  = !modules || modules.has('meetings');
 *
 *   const { permissions, isAdmin } = useModulePermissions();
 *   const canWrite = permissions?.meetings?.can_write ?? false;
 */
const ModuleAccessContext = createContext({ modules: null, permissions: {}, isAdmin: false });

/**
 * Returns the module key Set (Set<string> | null).
 * Backward-compatible — existing consumers don't need changes.
 */
export function useModuleAccess() {
  const ctx = useContext(ModuleAccessContext);
  // Support both old (bare Set) and new (object) context values
  if (ctx instanceof Set || ctx === null) return ctx;
  return ctx.modules ?? null;
}

/**
 * Returns the full context: { modules, permissions, isAdmin }.
 * permissions is a map: { module_key: { can_read, can_write, can_approve } }
 */
export function useModulePermissions() {
  const ctx = useContext(ModuleAccessContext);
  if (ctx instanceof Set || ctx === null) return { modules: ctx, permissions: {}, isAdmin: false };
  return ctx;
}

export default ModuleAccessContext;
